"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Loader2, AlertCircle, Users } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useStore } from "@/lib/store";

const CLIENT_COLORS = [
  "#eb5757",
  "#f2994a",
  "#f2c94c",
  "#26b24b",
  "#4ea7fc",
  "#5e6ad2",
  "#a55cff",
  "#ff8ae2",
  "#4cc3c7",
];

const COMMON_ICONS = ["◆", "🏁", "💳", "☀️", "📚", "🧠", "⚡", "🎯", "🏊", "🎥", "⧗"];

function deriveKey(name: string): string {
  const base = name
    .replace(/[^A-Za-z0-9 ]/g, "")
    .split(/\s+/)
    .filter(Boolean);
  if (!base.length) return "";
  if (base.length === 1) return base[0].slice(0, 4).toUpperCase();
  return base
    .slice(0, 3)
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 4);
}

export function CreateClientModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const createTeam = useStore((s) => s.createTeam);

  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [icon, setIcon] = useState("◆");
  const [color, setColor] = useState(CLIENT_COLORS[5]);
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setKey("");
      setKeyTouched(false);
      setIcon("◆");
      setColor(CLIENT_COLORS[5]);
      setDescription("");
      setError(null);
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open]);

  // Auto-derive key from name unless user typed their own
  useEffect(() => {
    if (!keyTouched) setKey(deriveKey(name));
  }, [name, keyTouched]);

  const keyValid = useMemo(() => /^[A-Z][A-Z0-9]{1,5}$/.test(key), [key]);
  const canSubmit = name.trim().length > 0 && keyValid && !submitting;

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await createTeam({
        name: name.trim(),
        key: key.toUpperCase(),
        icon,
        color,
      });
      // createTeam currently doesn't accept description; set via PATCH after?
      // Skipping for v1.
      void description;
      onClose();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : err && typeof err === "object"
          ? JSON.stringify(err, null, 2)
          : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose} width={520}>
      <div
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            void submit();
          }
        }}
      >
        <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--border)]">
          <Users size={14} className="text-[var(--accent)]" />
          <h3 className="text-[13.5px] font-semibold">New team</h3>
          <span className="ml-auto text-[10.5px] text-[var(--fg-muted)]">
            Tasks will use the prefix you choose below
          </span>
        </div>

        <div className="p-5 space-y-4">
          {/* Preview */}
          <div className="flex items-center gap-3 p-3 rounded-md bg-[var(--bg-secondary)] border border-[var(--border)]">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-[16px] shrink-0"
              style={{ background: `${color}22`, color }}
            >
              {icon || "◆"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold truncate">
                {name.trim() || "Team name"}
              </div>
              <div className="text-[11px] text-[var(--fg-tertiary)] font-mono truncate">
                Tasks: {key ? `${key}-1, ${key}-2, ${key}-3…` : "KEY-1, KEY-2…"}
              </div>
            </div>
          </div>

          <Field label="Name">
            <input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="VAR — Pedro Lima, PinPag, 77Sol, Acme Corp…"
              maxLength={80}
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] outline-none focus:border-[var(--accent)]"
            />
          </Field>

          <div className="grid grid-cols-[140px_1fr] gap-3">
            <Field label="Task prefix">
              <input
                value={key}
                onChange={(e) => {
                  setKeyTouched(true);
                  setKey(e.target.value.toUpperCase().slice(0, 6));
                }}
                placeholder="VAR"
                maxLength={6}
                className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px] font-mono uppercase outline-none focus:border-[var(--accent)]"
              />
              {key && !keyValid && (
                <p className="text-[10.5px] text-[var(--orange)] mt-1">
                  Uppercase letters/digits, 2–6 chars, starting with a letter.
                </p>
              )}
            </Field>

            <Field label="Icon">
              <div className="flex items-center gap-1.5 flex-wrap">
                <input
                  value={icon}
                  onChange={(e) => setIcon(e.target.value.slice(0, 2))}
                  placeholder="◆"
                  maxLength={2}
                  className="w-12 bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-2 py-1.5 text-[14px] text-center outline-none focus:border-[var(--accent)]"
                />
                {COMMON_ICONS.map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setIcon(i)}
                    className={`w-7 h-7 rounded-md text-[14px] hover:bg-[var(--bg-hover)] ${
                      icon === i ? "bg-[var(--bg-active)]" : ""
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          <Field label="Color">
            <div className="flex flex-wrap gap-1.5">
              {CLIENT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className="w-7 h-7 rounded-md border-2 transition-all"
                  style={{
                    background: c,
                    borderColor: color === c ? "var(--fg)" : "transparent",
                    transform: color === c ? "scale(1.1)" : "scale(1)",
                  }}
                />
              ))}
            </div>
          </Field>

          <Field label="Description" optional>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this team owns. Used as context for agents acting on this team's tasks/docs."
              rows={2}
              maxLength={240}
              className="w-full bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md px-3 py-1.5 text-[12.5px] outline-none focus:border-[var(--accent)] resize-none"
            />
          </Field>

          {error && (
            <div className="rounded-md border border-[var(--red)]/30 bg-[var(--red)]/8 p-3 flex items-start gap-2">
              <AlertCircle size={13} className="text-[var(--red)] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-[var(--fg)]">
                  Couldn&apos;t create team
                </div>
                <pre className="text-[11.5px] text-[var(--fg-secondary)] mt-1 whitespace-pre-wrap break-words font-mono">
                  {error}
                </pre>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--border)] bg-[var(--bg-secondary)]">
          <div className="text-[11px] text-[var(--fg-muted)]">
            <kbd className="kbd">⌘</kbd> <kbd className="kbd">Enter</kbd> to create
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-1.5 rounded-md text-[12.5px] text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => void submit()}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] disabled:opacity-40 text-white text-[12.5px] font-medium"
            >
              {submitting ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Plus size={12} />
              )}
              {submitting ? "Creating…" : "Create team"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Field({
  label,
  optional,
  children,
}: {
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10.5px] uppercase tracking-wide text-[var(--fg-muted)] font-medium flex items-center gap-1">
        {label}
        {optional && <span className="normal-case">· optional</span>}
      </div>
      {children}
    </div>
  );
}
